import React from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import SVGIcon from '../components/SVGIcon';
import { MentionBaseProvider } from '../editor/mentions/MentionBase';
import PostMention from '../editor/mentions/PostMention';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { TopicActivityAction } from '../graphql/generated/graphql';
import commentIcon from '../icons/non-prefilled/16x16/comment.svg';
import editPostIcon from '../icons/non-prefilled/16x16/editPost.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import replyIcon from '../icons/non-prefilled/16x16/reply.svg';
import thanksIcon from '../icons/non-prefilled/16x16/thanks.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verifiedFilled.svg';
import CommentPreviewText from '../post/comment/CommentPreviewText';
import { useGetUser } from '../user/useAllUsers';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';
import commentThreadPath from '../utils/url/commentThreadPath';
import historyPath from '../utils/url/historyPath';
import postPath from '../utils/url/postPath';
import ActivityItem from './ActivityItem';
import * as styles from './ActivityList.module.scss';

export const ACTIVITY_LIST_PREFETCH_DISTANCE = 3000;

const ACTIVITIES_FRAGMENT = gql(`
  fragment ActivityList_activities on TopicActivity {
    actedAt
    action
    actors {
      id
      ...useGetUser_user
    }
    post {
      id
      title
      ...postPath_post
    }
    source {
     id
      ... on Comment {
        id
        content
        thread {
          id
          ...commentThreadPath_thread
        }
      }
      ... on History {
        id
        versionFrom
        versionTo
      }
    }
  }
`);

interface ActivityListProps {
  activities: DocumentType<typeof ACTIVITIES_FRAGMENT>[];
  fetchMore: (() => void) | null;
}

const ActivityList = ({ activities, fetchMore }: ActivityListProps) => {
  const getUser = useGetUser();

  if (activities.length === 0 && !fetchMore) return null;

  const activitiesToShow = activities.filter((activity) => {
    const isEmptyEdit =
      activity.action === TopicActivityAction.Edit &&
      activity.actors.length === 0;
    return !isEmptyEdit;
  });

  return (
    <div
      className={styles.container}
      role="feed"
      aria-label="Recent activities"
    >
      <MentionBaseProvider containerType="activity">
        {activitiesToShow.map((activity, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <ErrorBoundary name="activity" key={index}>
            <SingleActivity
              activity={activity}
              getUser={getUser}
              aria-posinset={index + 1}
            />
          </ErrorBoundary>
        ))}
        <InfiniteScrollWaypoint
          fetchMore={fetchMore}
          prefetchDistance={ACTIVITY_LIST_PREFETCH_DISTANCE}
        />
      </MentionBaseProvider>
    </div>
  );
};

interface SingleActivityProps {
  'aria-posinset'?: number;
  activity: DocumentType<typeof ACTIVITIES_FRAGMENT>;
  getUser: ReturnType<typeof useGetUser>;
}

const SingleActivity = ({
  activity,
  getUser,
  'aria-posinset': ariaPosinset,
}: SingleActivityProps) => {
  const actors = activity.actors.map(getUser);
  const postMention = (
    <PostMention
      id={activity.post.id}
      content={activity.post.title}
      link={false}
    />
  );
  const postHref = postPath({
    title: activity.post.title,
    id: activity.post.id,
  });
  const comment =
    activity.source?.__typename === 'Comment' ? activity.source : null;
  const commentHref = comment && commentThreadPath(comment.thread);
  const commentPreview = comment && (
    <div className={styles.commentPreview}>
      <CommentPreviewText context={comment.content} maxLines={3} />
    </div>
  );

  switch (activity.action) {
    case TopicActivityAction.Publish:
      return (
        <ActivityItem
          actors={actors}
          icon={<SVGIcon className={styles.publishIcon}>{publishIcon}</SVGIcon>}
          timestamp={activity.actedAt}
          href={postHref}
          linkArrow="never"
          description={
            <div className={styles.activityDescription}>
              Published a new post {postMention}
            </div>
          }
          aria-posinset={ariaPosinset}
        />
      );
    case TopicActivityAction.Thank:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.thanksIcon}>{thanksIcon}</SVGIcon>}
          href={postHref}
          linkArrow="never"
          description={
            <div className={styles.activityDescription}>
              Thanked {postMention}
            </div>
          }
          aria-posinset={ariaPosinset}
        />
      );
    case TopicActivityAction.Comment: {
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.commentIcon}>{commentIcon}</SVGIcon>}
          href={commentHref ?? undefined}
          linkArrow="never"
          description={
            <div className={styles.activityDescription}>
              Commented {postMention}
              {commentPreview}
            </div>
          }
          aria-posinset={ariaPosinset}
        />
      );
    }
    case TopicActivityAction.Reply:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.replyIcon}>{replyIcon}</SVGIcon>}
          href={commentHref ?? undefined}
          linkArrow="never"
          description={
            <div className={styles.activityDescription}>
              Commented {postMention}
              {commentPreview}
            </div>
          }
          aria-posinset={ariaPosinset}
        />
      );
    case TopicActivityAction.Verify:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.verifiedIcon}>{verifiedIcon}</SVGIcon>
          }
          href={postHref}
          linkArrow="never"
          description={
            <div className={styles.activityDescription}>
              Verified {postMention}
            </div>
          }
          aria-posinset={ariaPosinset}
        />
      );
    case TopicActivityAction.Edit: {
      if (!activity.actors.length) {
        return null;
      }
      const history =
        activity.source?.__typename === 'History' ? activity.source : null;
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.editPostIcon}>{editPostIcon}</SVGIcon>
          }
          description={
            <div className={styles.activityDescription}>
              Edited {postMention}
            </div>
          }
          linkArrow="never"
          href={
            history
              ? historyPath({
                  post: activity.post,
                  versionFrom: history.versionFrom,
                  versionTo: history.versionTo,
                })
              : undefined
          }
          aria-posinset={ariaPosinset}
        />
      );
    }
    default:
      exhaustiveMatchGuard(activity.action);
      return null;
  }
};

export default ActivityList;
